/**
 * ImpersonationBanner — sticky strip rendered at the top of the Admin
 * surface when the current /api/me response indicates the founder is
 * impersonating another user.
 *
 * Hidden when no impersonation is active.
 *
 * "Exit impersonation" clears the localStorage flag, removes the
 * X-Impersonate-User-Id header on all future requests, and reloads
 * the page so the admin tabs re-render under the real founder role.
 */
import React from "react";
import { UserCog, X } from "lucide-react";
import { stopImpersonation } from "../../lib/api";

export const ImpersonationBanner = ({ me, onExit }) => {
  if (!me || !me.impersonated_as_user_id) return null;

  const handleExit = () => {
    stopImpersonation();
    if (onExit) onExit();
    // Reload so /api/me is re-fetched without the header and all
    // tab gates re-evaluate against the real founder role.
    window.location.reload();
  };

  return (
    <div
      className="bg-amber-50 border-y-2 border-amber-300 text-amber-900"
      data-testid="impersonation-banner"
    >
      <div className="ek-container py-3 flex items-center justify-between gap-4 text-[13px]">
        <div className="flex items-center gap-3">
          <UserCog size={18} className="text-amber-700" />
          <div>
            <span className="font-semibold">Acting as</span>{" "}
            <span className="font-mono text-amber-800">{me.email}</span>
            <span className="text-amber-700"> ({me.platform_role})</span>
            {me.real_email && (
              <span className="text-amber-700 ml-2">
                — real founder:{" "}
                <span className="font-mono">{me.real_email}</span>
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleExit}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-900 text-amber-50 text-[12px] font-medium tracking-[0.06em] uppercase hover:bg-amber-800 transition-colors"
          data-testid="impersonation-banner-exit"
        >
          <X size={14} />
          Exit impersonation
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
